// @ts-check
// Contagem do público (contrato 09 de público, seção 9): cards e etapas, quantos recebem por canal ("cerca de" quando é
// estimativa), quem fica de fora e por quê, avisos e a amostra dos cards mais novos, com "Ver todos". Fala com leitor de
// tela por aria-live.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { findOption } from '../../lib/field-metadata.js';
import { formatNumber } from '../../lib/format.js';
import { Tag } from '../../ui/chip.js';
import { Icon } from '../../ui/icon.js';
import { Skeleton } from '../../ui/feedback.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/audiences.js').AudiencePreview} AudiencePreview
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {{ status: 'idle' | 'loading' | 'updating' | 'ready' | 'error' | 'forbidden', preview: AudiencePreview | null, error: string | null }} PreviewState
 */

/** @param {number} value */
const count = (value) => formatNumber(value);

/**
 * @param {{ state: PreviewState, stageField: FieldMetadata | undefined, channels: Array<'EMAIL' | 'WHATSAPP'>, showRecipients: boolean,
 *   readOnly: boolean, onRetry: () => void, onRefresh: () => void, onSeeAll: () => void, isMobileOpen?: boolean, onToggleMobile?: () => void }} props
 */
export function AudienceCountPanel({ state, stageField, channels, showRecipients, readOnly, onRetry, onRefresh, onSeeAll, isMobileOpen = false, onToggleMobile }) {
  const [isExcludedOpen, setIsExcludedOpen] = useState(false);
  const preview = state.preview;
  const total = preview?.cards.total ?? 0;
  const firstChannel = preview?.recipients[0];
  const mobileSummary = preview
    ? (firstChannel && showRecipients
      ? t('audience.count.mobileBar', { cards: count(total), recipients: `${firstChannel.isEstimate ? t('audience.count.approximate') + ' ' : ''}${count(firstChannel.eligible)}` })
      : t('audience.count.cards', { count: total, formatted: count(total) }))
    : t('audience.count.updating');
  return html`<section class="audience-count-panel" aria-live="polite" data-status=${state.status} data-mobile-open=${isMobileOpen ? 'true' : undefined}>
    <button type="button" class="audience-count-mobile-bar" aria-expanded=${isMobileOpen ? 'true' : 'false'} onClick=${onToggleMobile}>
      <span>${mobileSummary}</span>
      <${Icon} name=${isMobileOpen ? 'IconChevronDown' : 'IconChevronUp'} size=${16} />
    </button>
    <div class="audience-count-body">
      ${renderBody()}
    </div>
  </section>`;

  function renderBody() {
    if (state.status === 'forbidden') return html`<p class="audience-count-message">${t('audience.count.noPermission')}</p>`;
    if (state.status === 'error' && !preview) {
      return html`<div class="audience-count-message" role="alert">
        <p>${t('audience.count.error')}</p>
        <button type="button" class="light-button" onClick=${onRetry}><${Icon} name="IconRefresh" size=${14} /><span>${t('audience.count.retry')}</span></button>
      </div>`;
    }
    if (!preview && readOnly && state.status === 'idle') {
      return html`<div class="audience-count-message">
        <button type="button" class="light-button" onClick=${onRefresh}><${Icon} name="IconRefresh" size=${14} /><span>${t('audience.count.refresh')}</span></button>
      </div>`;
    }
    if (!preview) {
      return html`<div class="audience-count-skeleton" aria-busy="true">
        <${Skeleton} width="60%" height=${28} />
        <${Skeleton} width="90%" height=${14} />
        <${Skeleton} width="80%" height=${14} />
      </div>`;
    }
    return html`<div class="audience-count-content" data-updating=${state.status === 'updating' ? 'true' : undefined}>
      <div class="audience-count-header">
        <strong class="audience-count-total">${t('audience.count.cards', { count: total, formatted: count(total) })}</strong>
        ${state.status === 'updating' ? html`<span class="audience-count-updating">${t('audience.count.updating')}</span>` : null}
        ${readOnly ? html`<button type="button" class="light-button" onClick=${onRefresh}><${Icon} name="IconRefresh" size=${14} /><span>${t('audience.count.refresh')}</span></button>` : null}
      </div>
      ${state.status === 'error' ? html`<p class="audience-count-warning" role="alert">${t('audience.count.error')} <button type="button" class="audience-link-button" onClick=${onRetry}>${t('audience.count.retry')}</button></p>` : null}
      ${total === 0 ? html`<p class="audience-count-empty">${t('audience.count.empty')}</p>` : null}
      ${preview.cards.byStage.length > 0
        ? html`<div class="audience-count-stages">
            ${preview.cards.byStage.map((entry) => {
              const option = stageField ? findOption(stageField, entry.stage) : undefined;
              return html`<${Tag} key=${entry.stage} color=${option?.color} text=${`${translateMetadataLabel(option?.label ?? entry.stage)} ${count(entry.count)}`} />`;
            })}
          </div>`
        : null}
      ${showRecipients ? preview.recipients.map((channel) => html`<div class="audience-count-channel" key=${channel.channel}>
        <div class="audience-count-channel-line">
          <${Icon} name=${channel.channel === 'EMAIL' ? 'IconMail' : 'IconBrandWhatsapp'} size=${16} />
          <span>${t(`audience.count.recipients.${channel.channel}`, {
            count: channel.eligible, formatted: `${channel.isEstimate ? `${t('audience.count.approximate')} ` : ''}${count(channel.eligible)}`,
          })}</span>
          ${channel.isEstimate
            ? html`<span class="audience-count-info" title=${t('audience.count.estimateHint', { cards: count(channel.basedOnCards) })} aria-label=${t('audience.count.estimateHint', { cards: count(channel.basedOnCards) })}>
                <${Icon} name="IconInfoCircle" size=${14} />
              </span>`
            : null}
        </div>
        ${channel.purpose === 'SERVICE' && channel.serviceWithoutRelationship
          ? html`<p class="audience-count-note">${t('audience.count.serviceWithoutRelationship', { count: channel.serviceWithoutRelationship, formatted: count(channel.serviceWithoutRelationship) })}</p>`
          : null}
      </div>`) : null}
      ${showRecipients && preview.recipients.some((channel) => Object.keys(channel.excluded).length > 0 || channel.duplicates > 0)
        ? html`<div class="audience-count-excluded">
            <button type="button" class="audience-count-excluded-toggle" aria-expanded=${isExcludedOpen ? 'true' : 'false'} onClick=${() => setIsExcludedOpen(!isExcludedOpen)}>
              <${Icon} name=${isExcludedOpen ? 'IconChevronDown' : 'IconChevronRight'} size=${14} />
              <span>${t('audience.count.excludedTitle')}</span>
            </button>
            ${isExcludedOpen ? preview.recipients.map((channel) => html`<ul class="audience-count-reasons" key=${channel.channel} aria-label=${t(`audience.channels.${channel.channel}`)}>
              ${channels.length > 1 ? html`<li class="audience-count-reasons-channel">${t(`audience.channels.${channel.channel}`)}</li>` : null}
              ${Object.entries(channel.excluded).map(([reason, value]) => html`<li key=${reason}><span>${t(`deliveryReasons.${reason}`)}</span><strong>${count(value)}</strong></li>`)}
              ${channel.duplicates > 0 ? html`<li><span>${t('audience.count.duplicates')}</span><strong>${count(channel.duplicates)}</strong></li>` : null}
            </ul>`) : null}
          </div>`
        : null}
      ${preview.warnings.length > 0
        ? html`<ul class="audience-count-warnings">${preview.warnings.map((warning) => html`<li key=${warning.code}>
            <${Icon} name="IconAlertTriangle" size=${14} />
            <span>${t(`audience.warnings.${warning.code}`, { ...warning, formatted: typeof warning.count === 'number' ? count(warning.count) : '' })}</span>
          </li>`)}</ul>`
        : null}
      ${preview.sample.length > 0 ? html`<${AudienceSample} preview=${preview} stageField=${stageField} showRecipients=${showRecipients} onSeeAll=${onSeeAll} />` : null}
    </div>`;
  }
}

/**
 * Amostra dos cards mais novos, com o contato e o desfecho do primeiro canal.
 * @param {{ preview: AudiencePreview, stageField: FieldMetadata | undefined, showRecipients: boolean, onSeeAll: () => void }} props
 */
function AudienceSample({ preview, stageField, showRecipients, onSeeAll }) {
  return html`<div class="audience-sample">
    <div class="audience-sample-header">
      <span>${t('audience.sample.title')}</span>
      <button type="button" class="audience-link-button" onClick=${onSeeAll}>${t('audience.sample.seeAll')}</button>
    </div>
    <ul class="audience-sample-list">
      ${preview.sample.map((card) => {
        const option = stageField && card.stage ? findOption(stageField, card.stage) : undefined;
        const contact = card.recipients[0];
        const outcome = contact ? Object.values(contact.outcomes)[0] : undefined;
        return html`<li key=${card.cardId} class="audience-sample-row">
          <div class="audience-sample-card">
            <span class="audience-sample-name">${card.cardName || t('audience.sample.unnamed')}</span>
            ${option ? html`<${Tag} color=${option.color} text=${translateMetadataLabel(option.label)} />` : null}
          </div>
          <div class="audience-sample-contact">
            <span>${contact?.displayName || t('audience.sample.noContact')}</span>
            ${showRecipients && outcome
              ? html`<span class="audience-sample-outcome" data-status=${outcome.status}>
                  ${outcome.status === 'ELIGIBLE' ? t('audience.sample.willReceive') : t(`deliveryReasons.${outcome.reason}`)}
                </span>`
              : null}
          </div>
        </li>`;
      })}
    </ul>
  </div>`;
}
